import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Card,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useAppSelector, useAppDispatch } from "@hooks/redux";
import {
  updateHive,
  deleteHive,
  fetchApiariesAndGroups,
  setApiaryEdited,
} from "@store/slices/locationsSlice";
import SaveToolbar from "@components/common/SaveToolbar";
import UnsavedChangesDialog from "@components/common/UnsavedChangesDialog";
import { useUnsavedChanges } from "@hooks/useUnsavedChanges";
import { apiClient } from "@api/index";
import HiveFormFields, {
  HiveFormValues,
} from "@components/hives/HiveFormFields";

interface HiveType {
  id: number;
  name: string;
  type: string;
  description: string;
}

const HiveEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const { hives, apiaries, loadingState, error } = useAppSelector(
    (state) => state.locations,
  );

  const [showLoadingIcon, setShowLoadingIcon] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hiveColor, setHiveColor] = useState("#F8B133");
  const [hiveTypes, setHiveTypes] = useState<HiveType[]>([]);
  const [loadingHiveTypes, setLoadingHiveTypes] = useState(true);
  const [beeRaces, setBeeRaces] = useState<{ value: string; label: string }[]>([]);
  const [loadingBeeRaces, setLoadingBeeRaces] = useState(true);

  const hive = hives.find((h) => h.id === parseInt(id || "0"));

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<HiveFormValues>({
    defaultValues: {
      name: "",
      location_id: null,
      hive_type: 1,
      color: "#F8B133",
      bee_race: "",
      order: 0,
      label: "",
      notes: "",
    },
  });

  // Track form changes
  useEffect(() => {
    if (isDirty) {
      dispatch(setApiaryEdited(true));
    }
  }, [isDirty, dispatch]);

  // Navigation guard
  const { showDialog, confirmNavigation, cancelNavigation } = useUnsavedChanges({
    when: isDirty && !showLoadingIcon,
    message: "You have unsaved changes. Are you sure you want to leave?",
    onConfirm: () => {
      dispatch(setApiaryEdited(false));
    },
  });

  useEffect(() => {
    if (loadingState === "idle") {
      dispatch(fetchApiariesAndGroups());
    }
  }, [dispatch, loadingState]);

  useEffect(() => {
    const fetchHiveTypes = async () => {
      try {
        setLoadingHiveTypes(true);
        const response = await apiClient.get("/hive-types/");
        if (response.data.success) {
          setHiveTypes(response.data.data);
        } else {
          setHiveTypes([
            { id: 1, name: "Langstroth", type: "langstroth", description: "Standard Langstroth hive" },
            { id: 2, name: "Kenya Top Bar Hive", type: "top_bar", description: "Kenya Top Bar Hive" },
            { id: 3, name: "Dadant", type: "dadant", description: "Dadant hive" },
            { id: 4, name: "Log Hive", type: "log", description: "Traditional log hive" },
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch hive types:", error);
        setHiveTypes([
          { id: 1, name: "Langstroth", type: "langstroth", description: "Standard Langstroth hive" },
          { id: 2, name: "Kenya Top Bar Hive", type: "top_bar", description: "Kenya Top Bar Hive" },
          { id: 3, name: "Dadant", type: "dadant", description: "Dadant hive" },
          { id: 4, name: "Log Hive", type: "log", description: "Traditional log hive" },
        ]);
      } finally {
        setLoadingHiveTypes(false);
      }
    };

    fetchHiveTypes();
  }, []);

  useEffect(() => {
    const fetchBeeRaces = async () => {
      try {
        setLoadingBeeRaces(true);
        const response = await apiClient.get("/hives/bee_races/");
        if (response.data.success) {
          setBeeRaces(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch bee races:", error);
        setBeeRaces([
          { value: "african", label: "African (Apis mellifera scutellata)" },
          { value: "italian", label: "Italian (Apis mellifera ligustica)" },
          { value: "carnica", label: "Carnica (Apis mellifera carnica)" },
          { value: "buckfast", label: "Buckfast" },
        ]);
      } finally {
        setLoadingBeeRaces(false);
      }
    };

    fetchBeeRaces();
  }, []);

  useEffect(() => {
    if (hive) {
      setValue("name", hive.name);
      setValue("location_id", hive.location_id);
      setValue("hive_type", hive.hive_type || 1);
      setValue("color", hive.color || "#F8B133");
      setValue("bee_race", hive.bee_race || "");
      setValue("order", hive.order || 0);
      setValue("label", hive.label || "");
      setValue("notes", hive.notes || "");
      setHiveColor(hive.color || "#F8B133");
    }
  }, [hive, setValue]);

  const handleSaveHive = async (data: HiveFormValues) => {
    if (!hive || !data.location_id) {
      setErrorMessage("Please select an apiary");
      return;
    }

    setShowLoadingIcon(true);
    setErrorMessage("");

    try {
      const hiveData: any = {
        name: data.name,
        location_id: data.location_id,
        hive_type: data.hive_type,
        color: data.color,
        bee_race: data.bee_race,
        order: data.order,
        label: data.label,
        notes: data.notes,
      };

      await dispatch(updateHive({ id: hive.id, data: hiveData })).unwrap();
      dispatch(setApiaryEdited(false));
      navigate("/");
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to update hive");
    } finally {
      setShowLoadingIcon(false);
    }
  };

  const handleDeleteHive = async () => {
    if (!hive) return;

    if (window.confirm(`Are you sure you want to delete hive "${hive.name}"?`)) {
      setShowLoadingIcon(true);
      try {
        await dispatch(deleteHive(hive.id));
        dispatch(setApiaryEdited(false));
        navigate("/");
      } catch (err: any) {
        setErrorMessage(err.message || "Failed to delete hive");
      } finally {
        setShowLoadingIcon(false);
      }
    }
  };

  const handleColorChange = (color: string) => {
    setHiveColor(color);
    setValue("color", color, { shouldDirty: true });
  };

  if (loadingState === "pending" || (loadingState === "idle" && !hive)) {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    );
  }

  if (!hive) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Hive not found
        </Typography>
      </Container>
    );
  }

  if (!(hive.owner || hive.editable)) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Sorry, hive "{hive.name}" is not editable
        </Typography>
      </Container>
    );
  }

  const formValues = watch();
  const isValid = !errors.name && !!formValues.name && !!formValues.location_id;

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
      {/* Save Toolbar */}
      <SaveToolbar
        onSave={handleSubmit(handleSaveHive)}
        onDelete={hive?.owner ? handleDeleteHive : undefined}
        saveDisabled={!isValid || showLoadingIcon || apiaries.length === 0}
        loading={showLoadingIcon}
        showDelete={hive?.owner}
      />

      <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
        Edit Hive: {hive.name}
      </Typography>

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setErrorMessage("")}
        >
          {errorMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(handleSaveHive)}>
            <HiveFormFields
              control={control}
              errors={errors}
              apiaries={apiaries}
              hiveTypes={hiveTypes}
              beeRaces={beeRaces}
              loadingHiveTypes={loadingHiveTypes}
              loadingBeeRaces={loadingBeeRaces}
              hiveColor={hiveColor}
              onColorChange={handleColorChange}
              locationDisabled={!hive.owner}
              showOrder
            />
          </form>
        </Box>
      </Card>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </Container>
  );
};

export default HiveEdit;
